/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock3, CreditCard, X } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { Badge } from "../../../components/ops/Badge";
import { useConfirm } from "../../../components/ops/ConfirmProvider";
import { DataToolbar } from "../../../components/ops/DataToolbar";
import { EmptyState } from "../../../components/ops/EmptyState";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Pagination } from "../../../components/ops/Pagination";
import { useToast } from "../../../components/ops/ToastProvider";
import { usePagedList } from "../../../components/ops/usePagedList";

const statusTone = { active: "success", pending: "neutral", past_due: "warning", paused: "neutral", cancelled: "danger" };
const intervalLabels = { monthly: "mo", quarterly: "qtr", annual: "yr", one_time: "one-time" };

export function MembershipPage() {
  const { accessToken } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [plans, setPlans] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resumingId, setResumingId] = useState("");
  const [planSearch, setPlanSearch] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/memberships/plans"),
      api.get("/memberships/enrollments"),
      api.get("/checkout"),
    ])
      .then(([plansResponse, enrollmentsResponse, checkoutResponse]) => {
        setPlans(plansResponse.data.plans || []);
        setEnrollments(enrollmentsResponse.data.enrollments || []);
        setCheckouts(checkoutResponse.data.checkouts || []);
      })
      .catch(() => toast.error("Unable to load membership plans"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [accessToken]);

  const activeEnrollments = useMemo(
    () => [...enrollments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [enrollments],
  );
  const pendingCheckouts = useMemo(
    () => checkouts.filter((checkout) => checkout.plan && ["pending", "payment_pending"].includes(checkout.status)),
    [checkouts],
  );

  const enrollmentPages = usePagedList(activeEnrollments, {
    search: "",
    searchFields: [],
    pageSize: 6,
  });

  const planPages = usePagedList(plans, {
    search: planSearch,
    searchFields: ["name", "description"],
    pageSize: 6,
  });

  const cancel = async (enrollment) => {
    const ok = await confirm({
      title: "Cancel this membership?",
      message: `This will cancel the ${enrollment.plan?.name || "membership"} for ${enrollment.player?.firstName || "this player"}.`,
      confirmLabel: "Cancel membership",
      tone: "danger",
    });
    if (!ok) return;

    try {
      const response = await api.patch(`/memberships/enrollments/${enrollment._id}/cancel`);
      setEnrollments(enrollments.map((e) => (e._id === response.data.enrollment._id ? response.data.enrollment : e)));
      toast.success("Membership cancelled.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to cancel this enrollment");
    }
  };

  const resumePayment = async (checkout) => {
    setResumingId(checkout._id);
    try {
      const response = await api.post("/payments/session", { checkoutId: checkout._id });
      window.location.href = response.data.alreadyPaid ? `/checkout/success?reference=${checkout.reference}` : response.data.checkoutUrl;
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to resume this payment");
      setResumingId("");
    }
  };

  return (
    <div>
      <PageHeader title="Membership" subtitle="Review your plans and enroll your players" />

      <div className="ops-card" style={{ marginBottom: 20 }}>
        <h2 className="ops-card-title">My memberships</h2>
        {loading ? (
          <p className="ops-loading">Loading your memberships…</p>
        ) : activeEnrollments.length === 0 && pendingCheckouts.length === 0 ? (
          <EmptyState icon={CreditCard} title="No memberships yet" description="Choose a plan below to get started." />
        ) : (
          <>
            {pendingCheckouts.length > 0 && (
              <ul className="ops-list" style={{ marginBottom: activeEnrollments.length > 0 ? 10 : 0 }}>
                {pendingCheckouts.map((checkout) => (
                  <li key={checkout._id} className="ops-list-item">
                    <div className="ops-list-item-avatar"><Clock3 size={16} /></div>
                    <div className="ops-list-item-body">
                      <p className="ops-list-item-title">{checkout.plan?.name || "Membership plan"}</p>
                      <p className="ops-list-item-meta">
                        {checkout.player?.firstName} {checkout.player?.lastName} · ${(checkout.amountCents / 100).toFixed(2)} · not paid yet
                      </p>
                    </div>
                    <div className="ops-list-item-trailing">
                      <Badge tone="neutral">Payment pending</Badge>
                      <button
                        type="button"
                        className="ops-button ops-button--primary ops-button--small"
                        onClick={() => resumePayment(checkout)}
                        disabled={resumingId === checkout._id}
                      >
                        {resumingId === checkout._id ? "Opening…" : "Finish payment"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {activeEnrollments.length > 0 && <ul className="ops-list">
              {enrollmentPages.pageItems.map((enrollment) => (
                <li key={enrollment._id} className="ops-list-item">
                  <div className="ops-list-item-avatar"><CreditCard size={16} /></div>
                  <div className="ops-list-item-body">
                    <p className="ops-list-item-title">{enrollment.plan?.name || "Membership plan"}</p>
                    <p className="ops-list-item-meta">
                      {enrollment.player?.firstName} {enrollment.player?.lastName} · ${((enrollment.plan?.priceCents || 0) / 100).toFixed(2)}/{intervalLabels[enrollment.plan?.billingInterval] || enrollment.plan?.billingInterval || "month"}
                      {enrollment.status === "active" && enrollment.currentPeriodEnd && ` · renews ${new Date(enrollment.currentPeriodEnd).toLocaleDateString()}`}
                      {enrollment.status === "past_due" && " · your last payment didn't go through — update your card to avoid losing access"}
                    </p>
                  </div>
                  <div className="ops-list-item-trailing">
                    <Badge tone={statusTone[enrollment.status] || "neutral"}>{enrollment.status.replace("_", " ")}</Badge>
                    {["active", "past_due", "pending"].includes(enrollment.status) && (
                      <button
                        type="button"
                        className="ops-button ops-button--danger ops-button--small"
                        onClick={() => cancel(enrollment)}
                        aria-label="Cancel membership"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>}
            {activeEnrollments.length > 0 && <Pagination page={enrollmentPages.page} totalPages={enrollmentPages.totalPages} onPageChange={enrollmentPages.setPage} />}
          </>
        )}
      </div>

      <div className="ops-card">
        <h2 className="ops-card-title">Available plans</h2>
        {plans.length > 0 && (
          <DataToolbar search={planSearch} onSearchChange={setPlanSearch} searchPlaceholder="Search plans by name..." resultCount={planPages.totalItems} itemLabel="plans" />
        )}
        {loading ? (
          <p className="ops-loading">Loading plans…</p>
        ) : planPages.totalItems === 0 ? (
          <EmptyState icon={CreditCard} title="No plans found" description={plans.length === 0 ? "Membership plans will appear here once they're published." : "Try adjusting your search."} />
        ) : (
          <>
            <div className="ops-cards-grid">
              {planPages.pageItems.map((plan) => (
                <div key={plan._id} className="ops-entity-card">
                  <div className="ops-entity-card-header">
                    <div className="ops-entity-card-icon"><CreditCard size={20} /></div>
                    <div className="ops-entity-card-heading">
                      <h3 className="ops-entity-card-title">{plan.name}</h3>
                      <p className="ops-entity-card-subtitle">{plan.ageGroup === "all" ? "All ages" : plan.ageGroup}</p>
                    </div>
                  </div>
                  <div className="ops-entity-card-stats">
                    <div className="ops-entity-card-stat">
                      <span className="ops-entity-card-stat-label">Price</span>
                      <span className="ops-entity-card-stat-value">${(plan.priceCents / 100).toFixed(2)} / {intervalLabels[plan.billingInterval] || plan.billingInterval}</span>
                    </div>
                    <div className="ops-entity-card-stat">
                      <span className="ops-entity-card-stat-label">Sessions/mo</span>
                      <span className="ops-entity-card-stat-value">{plan.sessionsPerMonth}</span>
                    </div>
                  </div>
                  {plan.description && (
                    <div className="ops-entity-card-body">
                      <p>{plan.description}</p>
                    </div>
                  )}
                  {plan.features?.length > 0 && (
                    <div className="ops-entity-card-tags">
                      {plan.features.map((feature) => <span key={feature} className="ops-entity-card-tag">{feature}</span>)}
                    </div>
                  )}
                  <div className="ops-entity-card-actions">
                    <Link to={`/checkout/${plan.slug}`} className="ops-button ops-button--primary ops-button--small">
                      Get started
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <Pagination page={planPages.page} totalPages={planPages.totalPages} onPageChange={planPages.setPage} />
          </>
        )}
      </div>
    </div>
  );
}
